import Vue from 'vue'
import { cloneData } from './labels'
import { createSeedState } from './seed'
import {
  AssignmentException,
  BenefitProgram,
  BrowseAndGoState,
  ExceptionAction,
  MultiChoiceCardModel,
  OperationModel,
  SavedSelection
} from './types'

const STORAGE_KEY = 'browse-and-go-admin-v1'

let sequence = 0

function nextId(prefix: string) {
  sequence += 1
  return `${prefix}-${Date.now().toString(36)}-${sequence}`
}

function isState(value: unknown): value is BrowseAndGoState {
  if (!value || typeof value !== 'object') return false
  const state = value as BrowseAndGoState
  return Array.isArray(state.merchants) &&
    Array.isArray(state.categories) &&
    Array.isArray(state.operations) &&
    Array.isArray(state.cards) &&
    Array.isArray(state.programs) &&
    Array.isArray(state.selections) &&
    Array.isArray(state.clients) &&
    Array.isArray(state.exceptions)
}

function readState(): BrowseAndGoState {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return createSeedState()
    const parsed = JSON.parse(raw) as { version?: number; state?: unknown }
    if (parsed.version === 1 && isState(parsed.state)) return parsed.state
  } catch {
    return createSeedState()
  }
  return createSeedState()
}

function persist() {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: 1, state: bngState }))
  } catch {
    // Le navigateur peut refuser l'écriture. L'écran reste utilisable pour la session.
  }
}

/** État local, en attendant l'API Browse and Go. */
export const bngState = Vue.observable(readState())

export function resetBrowseAndGoState() {
  const seed = createSeedState()
  bngState.categories = seed.categories
  bngState.merchants = seed.merchants
  bngState.operations = seed.operations
  bngState.cards = seed.cards
  bngState.programs = seed.programs
  bngState.selections = seed.selections
  bngState.clients = seed.clients
  bngState.exceptions = seed.exceptions
  persist()
}

export function findOperation(id: string) {
  return bngState.operations.find(operation => operation.id === id)
}

export function findCard(id: string) {
  return bngState.cards.find(card => card.id === id)
}

export function merchantName(id: string) {
  const merchant = bngState.merchants.find(item => item.id === id)
  return merchant ? merchant.name : id
}

export function saveOperation(operation: OperationModel) {
  const copy = cloneData(operation)
  if (!copy.id) {
    copy.id = nextId('op')
    copy.code = copy.code || copy.id
    copy.family = 'OTHER'
    copy.builtin = false
  }
  const index = bngState.operations.findIndex(item => item.id === copy.id)
  if (index === -1) bngState.operations.push(copy)
  else bngState.operations.splice(index, 1, copy)
  persist()
  return copy.id
}

export function deleteOperation(id: string) {
  const operation = findOperation(id)
  if (!operation || operation.builtin) return
  const cardIds = new Set(bngState.cards.filter(card => card.operationModelId === id).map(card => card.id))
  bngState.operations = bngState.operations.filter(item => item.id !== id)
  bngState.cards = bngState.cards.filter(card => card.operationModelId !== id)
  bngState.exceptions = bngState.exceptions.filter(item => {
    if (item.objectType === 'OPERATION_MODEL' && item.objectId === id) return false
    if (item.objectType === 'MULTICHOICE_CARD_MODEL' && cardIds.has(item.objectId)) return false
    return true
  })
  persist()
}

export function saveCard(card: MultiChoiceCardModel) {
  const copy = cloneData(card)
  if (!copy.id) {
    copy.id = nextId('card')
    copy.builtin = false
  }
  const operation = findOperation(copy.operationModelId)
  if (!operation || operation.family !== 'URSSAF') copy.urssafEventIds = []
  const index = bngState.cards.findIndex(item => item.id === copy.id)
  if (index === -1) bngState.cards.push(copy)
  else bngState.cards.splice(index, 1, copy)
  persist()
  return copy.id
}

export function deleteCard(id: string) {
  const card = findCard(id)
  if (!card || card.builtin) return
  bngState.cards = bngState.cards.filter(item => item.id !== id)
  bngState.exceptions = bngState.exceptions.filter(item =>
    !(item.objectType === 'MULTICHOICE_CARD_MODEL' && item.objectId === id)
  )
  persist()
}

export function saveProgram(program: BenefitProgram) {
  const copy = cloneData(program)
  if (!copy.id) {
    copy.id = nextId('prog')
    copy.builtin = false
  }
  const rates: BenefitProgram['rates'] = {}
  copy.merchantIds.forEach(id => {
    rates[id] = copy.rates[id] || { commission: 8, discount: 5 }
  })
  copy.rates = rates
  const index = bngState.programs.findIndex(item => item.id === copy.id)
  if (index === -1) bngState.programs.push(copy)
  else bngState.programs.splice(index, 1, copy)
  if (copy.isDefault) setDefaultProgram(copy.id, false)
  persist()
  return copy.id
}

export function setDefaultProgram(id: string, shouldPersist = true) {
  bngState.programs.forEach(program => {
    program.isDefault = program.id === id
  })
  bngState.exceptions = bngState.exceptions.filter(item =>
    !(item.objectType === 'PROGRAM_BENEFITS_MODEL' && item.objectId === id && item.action === 'ADD')
  )
  if (shouldPersist) persist()
}

export function deleteProgram(id: string) {
  const program = bngState.programs.find(item => item.id === id)
  if (!program || program.isDefault) return
  bngState.programs = bngState.programs.filter(item => item.id !== id)
  bngState.exceptions = bngState.exceptions.filter(item =>
    !(item.objectType === 'PROGRAM_BENEFITS_MODEL' && item.objectId === id)
  )
  persist()
}

export function saveSelection(selection: SavedSelection) {
  const copy = cloneData(selection)
  copy.itemIds = Array.from(new Set(copy.itemIds))
  if (!copy.id) copy.id = nextId('sel')
  const index = bngState.selections.findIndex(item => item.id === copy.id)
  if (index === -1) bngState.selections.push(copy)
  else bngState.selections.splice(index, 1, copy)
  persist()
  return copy.id
}

export function deleteSelection(id: string) {
  bngState.selections = bngState.selections.filter(item => item.id !== id)
  persist()
}

function replaceObjectExceptions(
  clientId: string,
  objectType: AssignmentException['objectType'],
  objectIds: string[],
  action: ExceptionAction,
  enabled: boolean
) {
  const ids = new Set(objectIds)
  bngState.exceptions = bngState.exceptions.filter(item => {
    if (item.clientId !== clientId || item.objectType !== objectType) return true
    return !ids.has(item.objectId)
  })
  if (enabled) {
    objectIds.forEach(objectId => {
      bngState.exceptions.push({
        id: nextId('ex'),
        clientId,
        objectType,
        objectId,
        action
      })
    })
  }
  persist()
}

export function setCardExceptions(clientId: string, cardIds: string[], action: ExceptionAction, enabled: boolean) {
  replaceObjectExceptions(clientId, 'MULTICHOICE_CARD_MODEL', cardIds, action, enabled)
}

export function setOperationException(clientId: string, operationId: string, action: ExceptionAction, enabled: boolean) {
  replaceObjectExceptions(clientId, 'OPERATION_MODEL', [operationId], action, enabled)
}

export function setBenefitProgram(clientId: string, programId: string) {
  bngState.exceptions = bngState.exceptions.filter(item =>
    !(item.clientId === clientId && item.objectType === 'PROGRAM_BENEFITS_MODEL')
  )
  const isDefault = bngState.programs.some(program => program.id === programId && program.isDefault)
  if (!isDefault) {
    bngState.exceptions.push({
      id: nextId('ex'),
      clientId,
      objectType: 'PROGRAM_BENEFITS_MODEL',
      objectId: programId,
      action: 'ADD'
    })
  }
  persist()
}

export function setExpertMode(clientId: string, enabled: boolean) {
  const client = bngState.clients.find(item => item.id === clientId)
  if (!client) return
  client.expertMode = enabled
  persist()
}
